import { useQuery } from '@apollo/client';
import type { FC } from 'react';

import {
  EmptyState,
  ModuleHeader,
  UpsellBannerWithIcon,
  WarningMessageBox as Error,
} from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import { DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE } from '../../config/media';
import type { AboutUsSubpageMediaFragment } from '../../graphql/fragment/about-us-media-fragment.gql-types';
import { AboutUsSubpageMediaRefetchQueryDocument } from '../../graphql/queries/about-us-subpage-media-refetch-query.gql-types';

import EmptyStateIcon from './assets/video-photo-upload.svg';
import { GalleryPreview } from './gallery-preview';

type GalleryPreviewContainerProps = {
  media: AboutUsSubpageMediaFragment;
  mediaItemsNumber?: number;
};

export const GalleryPreviewContainer: FC<GalleryPreviewContainerProps> = ({
  media: initialMedia,
  mediaItemsNumber = DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE,
}) => {
  const { pageContext } = usePageContext();

  const { data, refetch: refetchMedia } = useQuery(
    AboutUsSubpageMediaRefetchQueryDocument,
    {
      variables: {
        id: pageContext.pageSlug,
        first: mediaItemsNumber,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const aboutUsEntityPage =
    data?.pagesAboutUs?.__typename === 'AboutEntity'
      ? data?.pagesAboutUs
      : null;

  const media = aboutUsEntityPage?.media || initialMedia;
  const galleryItemNodes =
    media?.__typename === 'EntityMediaConnection' ? media.edges : [];
  const galleryItemsEdges = galleryItemNodes?.map((edge) => edge?.node);
  const galleryItemsNodes = galleryItemsEdges?.filter((node) => !!node);

  if (!galleryItemsNodes) {
    return (
      <div data-testid="aboutus-detail-media">
        <ModuleHeader headlineCopyKey="EP_MEDIA_HEADLINE" />
        <Error
          headerText="EP_ERROR_HEADER"
          bodyText="EP_ERROR_BODY"
          buttonText="EP_ERROR_RELOAD_CTA"
          onClick={() => refetchMedia()}
        />
      </div>
    );
  }

  if (pageContext.upsellActive) {
    return (
      <div data-testid="aboutus-detail-media">
        <ModuleHeader headlineCopyKey="EP_MEDIA_HEADLINE" />
        <UpsellBannerWithIcon
          headerCopyKey="EP_UPSELL_GALLERY_ABOUT_US_HEADER"
          bodyCopyKey="EP_UPSELL_GALLERY_ABOUT_US"
          moduleName="aboutusmodule"
        />
      </div>
    );
  }

  if (galleryItemsNodes?.length === 0 && pageContext.isEditor) {
    return (
      <div data-testid="aboutus-detail-media">
        <ModuleHeader headlineCopyKey="EP_MEDIA_HEADLINE" />
        <EmptyState
          headlineCopyKey="EP_EDIT_GALLERY_EMPTY_STATE_HEADER"
          textCopyKey="EP_EDIT_GALLERY_EMPTY_STATE_BODY"
          buttonProps={[
            {
              copyKey: 'EP_EDIT_GALLERY_EMPTY_STATE_CTA_IMAGE',
              to: `${pageContext.basePath}/${pageContext.pageSlug}/edit/gallery?type=image`,
            },
            {
              copyKey: 'EP_EDIT_GALLERY_EMPTY_STATE_CTA_VIDEO',
              to: `${pageContext.basePath}/${pageContext.pageSlug}/edit/gallery?type=video`,
            },
          ]}
          icon={EmptyStateIcon}
        />
      </div>
    );
  }

  if (galleryItemsNodes?.length) {
    return (
      <div data-testid="aboutus-detail-media">
        <ModuleHeader headlineCopyKey="EP_MEDIA_HEADLINE" />
        <GalleryPreview galleryItems={galleryItemsNodes} />
      </div>
    );
  }

  return null;
};
